import {
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "@/components/ui/dropdown-menu";
import { cx } from "@/utils/classes.utils";
import { DesktopItem } from "./DesktopItem.tsx";
import type { MenuCtx, ResponsiveMenuPage } from "./types";

export function DesktopDropdown<T extends string>({
  page,
  pagesMap,
  ctx,
}: {
  page?: ResponsiveMenuPage<T>;
  pagesMap: Map<string, ResponsiveMenuPage<T>>;
  ctx: MenuCtx;
}) {
  const items = page?.items ?? [];

  return (
    <>
      {items.map((item) => {
        switch (item.type) {
          case "label":
            return (
              <DropdownMenuLabel key={item.key} className="p-0 font-normal">
                {item.content}
              </DropdownMenuLabel>
            );

          case "separator":
            return <DropdownMenuSeparator key={item.key} />;

          case "action":
            return (
              <DropdownMenuItem
                key={item.key}
                disabled={item.disabled}
                className={cx(
                  "cursor-pointer disabled:cursor-not-allowed",
                  item.destructive && "text-destructive focus:text-destructive",
                )}
                onSelect={(e) => {
                  e.preventDefault();
                  void item.onSelect(ctx);
                }}
              >
                {item.icon ? (
                  <span className="mr-2 inline-flex">{item.icon}</span>
                ) : null}
                {item.label}
              </DropdownMenuItem>
            );

          case "page": {
            const target = pagesMap.get(item.pageId);
            if (!target) return null;
            return (
              <DropdownMenuSub key={item.key}>
                <DropdownMenuSubTrigger
                  className="cursor-pointer"
                  disabled={item.disabled}
                >
                  {item.icon ? (
                    <span className="mr-2 inline-flex">{item.icon}</span>
                  ) : null}
                  {item.label}
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent className="min-w-44">
                  {target.items.map((subItem) => (
                    <DesktopItem key={subItem.key} item={subItem} ctx={ctx} />
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            );
          }

          case "radio-group":
            return (
              <div key={item.key} className="min-w-44">
                {item.label ? (
                  <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
                    {item.label}
                  </div>
                ) : null}
                <DropdownMenuRadioGroup
                  value={item.value}
                  onValueChange={(v) => void item.onValueChange(v as T, ctx)}
                >
                  {item.options.map((opt) => (
                    <DropdownMenuRadioItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </DropdownMenuRadioItem>
                  ))}
                </DropdownMenuRadioGroup>
              </div>
            );

          default:
            return null;
        }
      })}
    </>
  );
}
